import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLComponentTypeManifest } from '../interfaces';
import { TpETLObjectTypes } from '../../etl-data-types/interfaces';
import { TpETLPipelineComponent, TpETLDryRun, TpETLDryRunComponentResult } from '../../interfaces';
import {
  copyETLDryRunObjectTableColumns,
  createConfigParamTypeExistingColumn,
  createConfigParamTypeMultipleChoice,
  createConfigParamTypeMultipleChoiceList,
  ETLDryRunObjectTableAddColumn,
} from '../utils';
import { addDryRunObjectTable, getETLComponentInputObjectTable, getETLTableObjectColumn } from '../../utils';
import { addETLDryRunError } from '../../etlDryRun';
import { VALTYPES, isMultiValuedType } from '../../../../data/interfaces';

type TpFieldOptions = {
  id: string;
  name: string;
  multiValue?: boolean;
};

const TAXON_INFORMATION_FIELDS: TpFieldOptions[] = [
  { id: 'taxid', name: 'NCBI Taxonomy ID' },
  { id: 'scientific_name', name: 'Scientific Name' },
  { id: 'genbank_common_name', name: 'GenBank Common Name' },
  { id: 'uniprot_name', name: 'UniProt Name' },
  { id: 'common_name', name: 'Common Name(s)', multiValue: true },
  { id: 'other_names', name: 'Other scientific names', multiValue: true },
  { id: 'parent_taxid', name: 'Parent Taxonomy ID' },
  { id: 'rank', name: 'Taxonomic Rank' },
];

/**
 * Validate the component configuration and add the expected output columns to the dry run object.
 * @param dryRun The current dry run state
 * @param comp The component configuration to validate
 * @returns Whether to abort the dry run or not.
 * In case of invalid configuration, the dry run is not aborted but errors are added to the dry run state that will be shown to the user.
 */
function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  const inputTableObj = getETLComponentInputObjectTable(dryRun, comp, 'sourceTable');
  const outputTableObj = addDryRunObjectTable(dryRun, comp.outputObjectIds['result'], comp);

  copyETLDryRunObjectTableColumns(inputTableObj, outputTableObj);

  const duplicateMethod = comp.params['handleDuplicates'];
  if (duplicateMethod === '') {
    addETLDryRunError(dryRun, `Please select a behaviour in case of duplicates`, comp.id, 'handleDuplicates');
  }

  const scopes: string[] | null | undefined = comp.params['scopes'];
  if (!scopes || scopes.length === 0) {
    addETLDryRunError(dryRun, `Please select at least one lookup field identifier`, comp.id, 'scopes');
  }

  const infoFields: string[] | null | undefined = comp.params['fields'];
  if (!infoFields || infoFields.length === 0) {
    addETLDryRunError(dryRun, `Please select at least one taxon information field`, comp.id, 'fields');
  }

  const sourceColId = comp.params['taxonQueryInput'];
  if (!sourceColId) {
    addETLDryRunError(dryRun, `Missing source taxon identifiers column`, comp.id, 'taxonQueryInput');
    return { abort: false };
  }
  const sourceColInfo = getETLTableObjectColumn(inputTableObj, sourceColId);

  const outputDataType = comp.params['outputDatatype'];
  if (!outputDataType) {
    addETLDryRunError(dryRun, `Please select an output datatype`, comp.id, 'outputDatatype');
  }

  const getColumnDataType = (field: TpFieldOptions): string => {
    const becomeMultiValue =
      duplicateMethod === 'multi_value' || field.multiValue || isMultiValuedType(sourceColInfo.dataType);
    if (outputDataType === VALTYPES.CATEGORICAL) {
      return becomeMultiValue ? VALTYPES.MULTICAT : VALTYPES.CATEGORICAL;
    } else {
      return becomeMultiValue ? VALTYPES.MULTISTRING : VALTYPES.STRING;
    }
  };

  // Add the columns to the dryrun output
  if (infoFields) {
    infoFields.forEach((field) => {
      const colInfo = TAXON_INFORMATION_FIELDS.find((f) => f.id === field);
      if (!colInfo) {
        throw `Internal error: unknown field ${field}`;
      }

      ETLDryRunObjectTableAddColumn(
        outputTableObj,
        `${sourceColInfo.identifier}_${field}`,
        colInfo.name,
        '', // No description yet
        getColumnDataType(colInfo),
      );
    });
  }

  return { abort: false };
}

export const manifestQueryTaxonInfo: TpETLComponentTypeManifest = {
  id: 'QueryTaxonInfo',
  name: 'Query Taxon Information',
  description: 'Given a column with some taxon identifiers, look up additional information about those taxa.',
  categoryId: 'biology',
  icon: <FontAwesomeIcon icon={['fad', 'dna']} />,
  inputs: [
    {
      type: TpETLObjectTypes.TABLE,
      id: 'sourceTable',
      name: 'Source Table',
      columns: [],
    },
  ],
  outputs: [
    {
      id: 'result',
      name: 'Result table',
      type: TpETLObjectTypes.TABLE,
      description: 'Table with additional taxon information added',
      columns: [],
    },
  ],
  params: [
    {
      id: 'taxonQueryInput',
      name: 'Source taxon identifiers',
      description: 'Column used containing the source taxon query identifiers.',
      paramType: createConfigParamTypeExistingColumn('sourceTable'),
    },
    {
      id: 'scopes',
      name: 'Lookup field identifiers',
      description:
        'One or more fields that match the meaning of the source taxon identifiers. This is the taxon metadata field used to query the taxon information database.',
      paramType: createConfigParamTypeMultipleChoiceList(
        [
          { id: 'taxid', name: 'NCBI Taxonomy ID' },
          { id: 'scientific_name', name: 'Scientific name' },
          { id: 'common_name', name: 'Common name' },
          { id: 'genbank_common_name', name: 'GenBank common name' },
          { id: 'uniprot_name', name: 'UniProt name' },
          { id: 'other_names', name: 'Other names' },
        ],
        ['taxid'], // Defaults
      ),
    },
    {
      id: 'fields',
      name: 'Taxon information to provide',
      description: 'One of more information fields to add as new columns.',
      paramType: createConfigParamTypeMultipleChoiceList(
        TAXON_INFORMATION_FIELDS.map((field) => ({
          id: field.id,
          name: field.multiValue ? `${field.name} (multi-value)` : field.name,
        })),
      ),
    },
    {
      id: 'outputDatatype',
      name: 'Output datatype',
      description:
        'The datatype of the output column(s). Will be made multi-value appropriately based on the data and duplicate handling method.',
      paramType: createConfigParamTypeMultipleChoice(
        [
          { id: VALTYPES.STRING, name: 'String' },
          { id: VALTYPES.CATEGORICAL, name: 'Categorical' },
        ],
        VALTYPES.STRING, // Default
      ),
    },
    {
      id: 'handleDuplicates',
      name: 'Handle Duplicates',
      description: 'Method to handle duplicate taxon entries.',
      paramType: createConfigParamTypeMultipleChoice(
        [
          {
            id: 'skip',
            name: 'Skip entries with duplicate hits',
          },
          {
            id: 'first_hit',
            name: 'Take first hit',
          },
          {
            // concatenate and multi_value look the same but (can) have a different datatype
            id: 'concatenate',
            name: 'Concatenate results in a semi-colon separated string',
          },
          {
            id: 'multi_value',
            name: 'Return all results as multi-value columns',
          },
        ],
        'first_hit', // Default
      ),
    },
  ],
  performDryRun,
};
